import { FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { UserRole } from '@shared/types';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  selectAccessControlCodes,
  selectCurrentUser,
  selectHasFiscalIntegration,
  selectNeedsFirstTimeOnboarding,
} from '../../../store/auth/authSelectors';
import { logout } from '../../../store/auth/authThunks';
import { AppLogo } from '../../common/AppLogo';
import { AppVersionWrapper } from '../../nav/AppVersionWrapper';
import {
  DASHBOARD_SIDEBAR_SECTIONS,
  SidebarLinkConfig,
  SidebarSectionConfig,
} from './sidebarConfig';
import { SidebarAccordionProvider, SidebarItem } from './SidebarItem';
import { SidebarUserProfile } from './SidebarUserProfile';

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------

export const canRenderLink = (
  config: SidebarLinkConfig,
  userRole: UserRole | null,
  accessControlCodes: string[],
  hasUnrestrictedAccess: boolean,
  hasFiscalIntegration: boolean,
): boolean => {
  if (
    config.allowedRoles &&
    (!userRole || !config.allowedRoles.includes(userRole))
  ) {
    return false;
  }
  if (config.requiresFiscalIntegration && !hasFiscalIntegration) {
    return false;
  }
  if (config.requiredControl) {
    return (
      hasUnrestrictedAccess ||
      accessControlCodes.includes(config.requiredControl)
    );
  }
  return true;
};

const filterLink = (
  config: SidebarLinkConfig,
  userRole: UserRole | null,
  accessControlCodes: string[],
  hasUnrestrictedAccess: boolean,
  hasFiscalIntegration: boolean,
): SidebarLinkConfig | null => {
  if (
    !canRenderLink(
      config,
      userRole,
      accessControlCodes,
      hasUnrestrictedAccess,
      hasFiscalIntegration,
    )
  ) {
    return null;
  }
  if (!config.children?.length) return config;

  const visibleChildren = config.children
    .map((child) =>
      filterLink(
        child,
        userRole,
        accessControlCodes,
        hasUnrestrictedAccess,
        hasFiscalIntegration,
      ),
    )
    .filter(Boolean) as SidebarLinkConfig[];

  return visibleChildren.length
    ? { ...config, children: visibleChildren }
    : null;
};

// ----------------------------------------------------------------
// Sidebar
// ----------------------------------------------------------------

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  /** Pass a custom set of sections to show in this sidebar instance.
   *  Defaults to the standard dashboard navigation. */
  sections?: SidebarSectionConfig[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  sections = DASHBOARD_SIDEBAR_SECTIONS,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const accessControlCodes = useAppSelector(selectAccessControlCodes);
  const hasFiscalIntegration = useAppSelector(selectHasFiscalIntegration);
  const needsFirstTimeOnboarding = useAppSelector(
    selectNeedsFirstTimeOnboarding,
  );

  const userRole = (currentUser?.role ?? null) as UserRole | null;
  const hasUnrestrictedAccess = Boolean(
    userRole && FULL_ACCESS_USER_ROLES.includes(userRole),
  );

  const visibleSections = useMemo(() => {
    const filtered = sections
      .map((section) => ({
        ...section,
        links: section.links
          .map((link) =>
            filterLink(
              link,
              userRole,
              accessControlCodes,
              hasUnrestrictedAccess,
              hasFiscalIntegration,
            ),
          )
          .filter(Boolean) as SidebarLinkConfig[],
      }))
      .filter((s) => s.links.length > 0);

    // Inject "Getting Started" link for first-time onboarding users
    if (needsFirstTimeOnboarding) {
      const gettingStarted: SidebarLinkConfig = {
        id: 'getting-started',
        icon: Sparkles,
        label: 'Getting Started',
        to: '/dashboard/getting-started',
      };
      const main = filtered.find((s) => s.id === 'main');
      if (main) {
        main.links = [gettingStarted, ...main.links];
      } else {
        filtered.unshift({ id: 'main', links: [gettingStarted] });
      }
    }

    return filtered;
  }, [
    sections,
    userRole,
    accessControlCodes,
    hasUnrestrictedAccess,
    hasFiscalIntegration,
    needsFirstTimeOnboarding,
  ]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/');
  };

  return (
    <div
      className={`relative flex h-full flex-col border-r border-gray-100 bg-white transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggleCollapse}
        className="absolute -right-3 top-6 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:border-pos-primary hover:text-pos-primary"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Logo / home button */}
      <button
        type="button"
        onClick={() => navigate('/sales')}
        className={`p-4 ${isCollapsed ? 'px-3' : ''}`}
      >
        <div
          className={`mb-6 flex items-center gap-2 ${isCollapsed ? 'justify-center' : ''}`}
        >
          {/* <img src={Logo} alt="Logo" className="h-6 w-6 shrink-0" /> */}
          <AppLogo size={24} />
          {!isCollapsed && <AppVersionWrapper className="text-lg font-bold" />}
        </div>
      </button>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <SidebarAccordionProvider>
          {visibleSections.map((section) => (
            <div key={section.id} className="mb-6">
              {section.title && !isCollapsed && (
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {section.title}
                </p>
              )}
              {section.links.map((link) => (
                <SidebarItem
                  key={link.id}
                  config={link}
                  isCollapsed={isCollapsed}
                />
              ))}
            </div>
          ))}
        </SidebarAccordionProvider>
      </div>

      {/* User profile */}
      <SidebarUserProfile
        firstName={currentUser?.first_name}
        lastName={currentUser?.last_name}
        role={currentUser?.role}
        isCollapsed={isCollapsed}
        onLogout={handleLogout}
      />
    </div>
  );
};
